/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { TokenMetadata } from '../lib/pinata';
import { 
    X, ArrowUpDown, Wallet, Info, Loader2, CheckCircle, RefreshCw, 
    Brain, ArrowRight, BarChart2, ExternalLink, Settings, 
    ChevronDown, ArrowLeft, ArrowLeftRight, Receipt, Zap, Shield, AlertTriangle
} from 'lucide-react';
import { useWallet } from '../providers/WalletProvider';
import { useTrade } from '../hooks/useTrade';
import { TradingChart } from './TradingChart';
import { ChartTimeframe } from '../types/charts';
import { TokenInsights } from './TokenInsights';
import { TokenSelectModal } from './TokenSelectModal';
import { SOL_MINT } from '../lib/solana/solana';
import { useSlippage } from '../hooks/use-slippage';
import { SLIPPAGE_PRESETS } from '../types/slippage';
import { formatCompact, formatCurrency } from '../lib/utils/format';

interface TradeModalProps {
  token: TokenMetadata;
  currentPrice: number;
  isOpen: boolean;
  onClose: () => void;
  initialSide?: 'BUY' | 'SELL';
}

type MobileTab = 'CHART' | 'TRADE';

export const TradeModal: React.FC<TradeModalProps> = ({ token, currentPrice, isOpen, onClose, initialSide = 'BUY' }) => {
  const { isConnected, connect, balance } = useWallet();
  const { value: globalSlippage, setSlippage: setGlobalSlippage } = useSlippage();

  const {
      amountIn, setAmountIn, setAmountByPercentage, isBuy, setIsBuy, tokenBalance, estimatedOut, minReceived,
      priceImpact, rate, isLoading, isSigning, isQuoting, error, txHash, executeTrade, reset, quote, internalQuote
  } = useTrade(token, currentPrice, initialSide as 'BUY' | 'SELL', globalSlippage);

  const [timeframe, setTimeframe] = useState<ChartTimeframe>('1H');
  const [showAi, setShowAi] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [mobileTab, setMobileTab] = useState<MobileTab>('TRADE'); 
  
  const [isTokenSelectOpen, setIsTokenSelectOpen] = useState(false);
  const [inputToken, setInputToken] = useState<any>({
      symbol: 'SOL',
      address: SOL_MINT,
      logoURI: 'https://cryptologos.cc/logos/solana-sol-logo.png'
  });

  const baseCurrency = token.properties?.chain || 'SOL';
  const isListed = !!token.properties?.mintAddress;
  const marketCap = currentPrice * (token.properties?.supply || 1000000000);

  if (!isOpen) return null;

  const handleTokenSelect = (selected: any) => {
      setInputToken(selected);
      setIsTokenSelectOpen(false);
  };

  const handleSwapDirection = () => {
      setIsBuy(!isBuy);
      setAmountIn(''); 
  };

  // --- SUCCESS VIEW ---
  if (txHash) {
      return (
        <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in" role="alertdialog" aria-modal="true">
            <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-8 text-center animate-fade-in-up border border-stone-200 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-400 to-emerald-500"></div>
                
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner ring-4 ring-green-50 animate-[bounce_0.5s_ease-out]">
                    <CheckCircle className="text-green-600" size={40} />
                </div>
                
                <h3 className="font-serif text-3xl font-bold text-stone-900 mb-2">Trade Confirmed</h3>
                <p className="text-stone-500 mb-6 font-medium">
                    Swap completed successfully on {baseCurrency}.
                </p>

                <div className="bg-stone-50 rounded-2xl p-4 mb-8 border border-stone-100">
                    <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-stone-500 font-bold uppercase text-xs">Status</span>
                        <span className="text-green-600 font-bold flex items-center gap-1"><CheckCircle size={12}/> Finalized</span>
                    </div>
                     <div className="flex justify-between items-center text-sm">
                        <span className="text-stone-500 font-bold uppercase text-xs">Transaction</span>
                        <a href={`#`} className="text-stone-900 font-mono hover:underline flex items-center gap-1">
                            {txHash.slice(0, 8)}... <ExternalLink size={10} />
                        </a>
                    </div>
                </div>
                
                <button 
                    onClick={() => { reset(); setShowConfirmation(false); onClose(); }}
                    className="w-full py-4 bg-stone-900 text-white rounded-xl font-bold hover:bg-nobel-gold hover:text-stone-900 transition-colors shadow-lg"
                >
                    Close Receipt
                </button>
            </div>
        </div>
      );
  }

  // --- CONFIRMATION VIEW ---
  if (showConfirmation) {
      const impactNum = Number(priceImpact);
      const isHighImpact = impactNum > 5;
      const isCriticalImpact = impactNum > 10;

      return (
        <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up border border-stone-200 flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-stone-100 flex items-center gap-3 bg-stone-50/50">
                    <button onClick={() => setShowConfirmation(false)} className="text-stone-400 hover:text-stone-900 p-1 rounded-full hover:bg-white transition-colors" aria-label="Back">
                        <ArrowLeft size={20} />
                    </button>
                    <h3 className="font-serif text-lg font-bold text-stone-900">Review Order</h3>
                </div>
                
                <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                    {/* Token Swap Visual */}
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center p-3 bg-stone-50 rounded-xl border border-stone-100">
                            <span className="text-2xl font-bold text-stone-900">{amountIn}</span>
                            <div className="flex items-center gap-2">
                                <img src={isBuy ? inputToken.logoURI : token.image} className="w-6 h-6 rounded-full" alt=""/>
                                <span className="font-bold text-sm text-stone-700">{isBuy ? inputToken.symbol : token.symbol}</span>
                            </div>
                        </div>
                        <div className="flex justify-center -my-3 relative z-10">
                            <div className="bg-white border border-stone-200 p-1.5 rounded-full shadow-sm">
                                <ArrowLeftRight size={14} className="rotate-90 text-stone-400" />
                            </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-stone-50 rounded-xl border border-stone-100">
                            <span className="text-2xl font-bold text-stone-900">{estimatedOut}</span>
                            <div className="flex items-center gap-2">
                                <img src={!isBuy ? inputToken.logoURI : token.image} className="w-6 h-6 rounded-full" alt=""/>
                                <span className="font-bold text-sm text-stone-700">{!isBuy ? inputToken.symbol : token.symbol}</span>
                            </div>
                        </div>
                    </div>

                    {/* Route Info */}
                    <div className="flex items-center justify-between px-2">
                        <span className="text-xs font-bold text-stone-400 uppercase">Route</span>
                        <span className="text-xs font-bold text-stone-700 flex items-center gap-1.5 bg-stone-100 px-2 py-1 rounded-md">
                            {internalQuote ? (
                                <><Zap size={12} className="text-amber-500 fill-current" /> Bonding Curve</>
                            ) : (
                                <><Zap size={12} className="text-green-500 fill-current" /> Jupiter Aggregator</>
                            )}
                        </span>
                    </div>

                    {/* Breakdown */}
                    <div className="space-y-3 pt-2 border-t border-stone-100">
                        <div className="flex justify-between text-sm">
                            <span className="text-stone-500">Rate</span>
                            <span className="font-mono text-stone-800">1 {isBuy ? inputToken.symbol : token.symbol} ≈ {rate} {isBuy ? token.symbol : inputToken.symbol}</span>
                        </div>
                        
                        <div className="flex justify-between text-sm">
                            <span className="text-stone-500">Price Impact</span>
                            <span className={`font-mono font-bold ${isCriticalImpact ? 'text-red-600' : isHighImpact ? 'text-amber-500' : 'text-green-600'}`}>
                                {priceImpact}%
                            </span>
                        </div>

                        <div className="flex justify-between text-sm">
                            <span className="text-stone-500">Network Cost</span>
                            <span className="font-mono text-stone-800">~0.000005 {baseCurrency}</span>
                        </div>

                        <div className="flex justify-between text-sm">
                            <span className="text-stone-500">Min. Received</span>
                            <span className="font-mono font-bold text-stone-900">{minReceived} {!isBuy ? inputToken.symbol : token.symbol}</span>
                        </div>
                    </div>

                    {/* Warnings */}
                    {isHighImpact && (
                        <div className="p-3 bg-red-50 border border-red-100 rounded-xl flex gap-2 items-start text-xs text-red-700">
                            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                            <div>
                                <strong>High Price Impact!</strong> You will lose a significant portion of your funds due to low liquidity.
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-5 border-t border-stone-100 bg-stone-50">
                    <button 
                        onClick={executeTrade}
                        disabled={isLoading || isSigning}
                        className="w-full py-4 bg-stone-900 text-white rounded-xl font-bold hover:bg-nobel-gold hover:text-stone-900 transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="animate-spin" size={20}/> 
                                {isSigning ? 'Waiting for Wallet...' : 'Swapping...'}
                            </>
                        ) : (
                            "Confirm Transaction"
                        )}
                    </button>
                </div>
            </div>
        </div>
      )
  }

  // --- MAIN TRADING VIEW ---
  return (
    <>
    <TokenSelectModal 
        isOpen={isTokenSelectOpen} 
        onClose={() => setIsTokenSelectOpen(false)}
        onSelect={handleTokenSelect}
        selectedTokenAddress={inputToken.address}
        chain={baseCurrency as 'SOL' | 'SUI'}
    />
    
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-md flex items-center justify-center animate-fade-in lg:p-4 pt-safe" onClick={onClose} role="dialog" aria-modal="true">
        <div 
            className="bg-[#F9F8F4] w-full lg:max-w-6xl h-full lg:h-auto lg:max-h-[90vh] lg:rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up border border-stone-200 flex flex-col relative mt-4 lg:mt-0"
            onClick={e => e.stopPropagation()}
        >
            {/* Header */}
            <div className="p-4 md:p-6 bg-white border-b border-stone-200 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-stone-100 p-0.5 border border-stone-100 flex-shrink-0 relative overflow-hidden shadow-sm">
                        {token.image ? <img src={token.image} alt={token.symbol} className="w-full h-full object-cover rounded-xl" /> : <div className="w-full h-full flex items-center justify-center text-stone-400 font-bold text-lg bg-stone-50 rounded-xl">{token.symbol?.[0]}</div>}
                        <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 border border-stone-100 shadow-sm">
                            <img src={token.properties?.chain === 'SUI' ? 'https://cryptologos.cc/logos/sui-sui-logo.png' : 'https://cryptologos.cc/logos/solana-sol-logo.png'} className="w-4 h-4" alt={token.properties?.chain} />
                        </div>
                    </div>
                    
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="font-serif text-2xl font-bold text-stone-900 leading-none">{token.name}</h2>
                            <span className="text-[10px] font-bold bg-stone-100 px-1.5 py-0.5 rounded text-stone-500 uppercase">{token.symbol}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1.5">
                            <div className="flex items-center gap-1">
                                <span className="font-mono text-sm font-bold text-stone-900">{formatCurrency(currentPrice)}</span>
                                <span className="text-[10px] text-green-600 font-bold bg-green-50 px-1 rounded">+2.4%</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button onClick={() => setShowSettings(!showSettings)} className={`p-2 rounded-xl transition-colors ${showSettings ? 'bg-stone-100 text-stone-900' : 'text-stone-400 hover:text-stone-900 hover:bg-stone-50'}`}>
                        <Settings size={20} />
                    </button>
                    <button onClick={onClose} className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                        <X size={24} />
                    </button>
                </div>
            </div>

            {/* Mobile Tabs */}
            <div className="lg:hidden flex border-b border-stone-200 bg-white shrink-0">
                <button onClick={() => setMobileTab('TRADE')} className={`flex-1 py-4 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 ${mobileTab === 'TRADE' ? 'text-stone-900 border-b-2 border-stone-900' : 'text-stone-400'}`}>
                    <ArrowLeftRight size={16} /> Trade
                </button>
                <button onClick={() => setMobileTab('CHART')} className={`flex-1 py-4 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 ${mobileTab === 'CHART' ? 'text-stone-900 border-b-2 border-stone-900' : 'text-stone-400'}`}>
                    <BarChart2 size={16} /> Chart
                </button>
            </div>
            
            <div className="flex flex-1 overflow-hidden relative flex-col lg:flex-row">
                {/* Left: Chart */}
                <div className={`flex-1 p-4 md:p-6 bg-white border-r border-stone-200 flex-col overflow-y-auto custom-scrollbar ${mobileTab === 'TRADE' ? 'hidden lg:flex' : 'flex'}`}>
                    <div className="flex items-center gap-2 mb-4 bg-stone-50 p-1 rounded-lg w-fit">
                        <button onClick={() => setShowAi(false)} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${!showAi ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-900'}`}>Chart</button>
                        <button onClick={() => setShowAi(true)} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1 ${showAi ? 'bg-stone-900 text-white shadow-sm' : 'text-stone-500 hover:text-stone-900'}`}><Brain size={12} /> AI Analysis</button>
                    </div>

                    {showAi ? (
                        <div className="flex-1 animate-fade-in"><TokenInsights token={token} currentPrice={currentPrice} /></div>
                    ) : (
                        <>
                            {isListed ? (
                                <TradingChart symbol={token.symbol} basePrice={currentPrice} timeframe={timeframe} onTimeframeChange={setTimeframe} tokenMint={token.properties?.mintAddress} launchDate={token.properties?.createdAt}/>
                            ) : (
                                <div className="w-full h-[300px] flex flex-col items-center justify-center bg-stone-50 rounded-2xl border border-stone-100 text-stone-400">
                                    <BarChart2 size={48} className="mb-4 opacity-50" />
                                    <h3 className="font-serif text-xl font-bold text-stone-600 mb-2">Chart Unavailable</h3>
                                    <p className="text-sm">This token is not yet listed on public DEXs.</p>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Right: Trade Form */}
                <div className={`w-full lg:w-[420px] bg-[#F9F8F4] flex flex-col relative ${mobileTab === 'CHART' ? 'hidden lg:flex' : 'flex'}`}>
                    <div className="p-4 md:p-6 space-y-4 overflow-y-auto flex-1 relative custom-scrollbar">
                        {showSettings && (
                            <div className="mb-4 p-4 bg-white rounded-xl border border-stone-200 animate-fade-in shadow-lg relative z-20">
                                <div className="flex justify-between items-center mb-3">
                                    <span className="text-xs font-bold text-stone-500 uppercase">Max Slippage</span>
                                    <span className="text-xs font-mono font-bold text-stone-900">{globalSlippage}%</span>
                                </div>
                                <div className="flex gap-2">
                                    {SLIPPAGE_PRESETS.map((val) => (
                                        <button key={val} onClick={() => setGlobalSlippage(val)} className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${globalSlippage === val ? 'bg-stone-900 border-stone-900 text-white' : 'bg-transparent border-stone-200 text-stone-500 hover:border-stone-400'}`}>{val}%</button>
                                    ))}
                                    <div className="relative flex-1">
                                        <input type="number" placeholder="Custom" value={globalSlippage} onChange={(e) => setGlobalSlippage(Number(e.target.value))} className="w-full h-full px-2 py-2 rounded-lg text-xs font-bold border border-stone-200 bg-stone-50 text-center focus:border-stone-900 outline-none transition-colors" />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] text-stone-400">%</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="relative flex flex-col gap-1">
                            {/* Input */}
                            <div className="bg-white p-4 rounded-2xl border border-stone-200 shadow-sm transition-colors focus-within:border-stone-400 relative z-0">
                                <div className="flex justify-between text-[10px] font-bold text-stone-400 uppercase mb-2 tracking-wide">
                                    <span>You Pay</span>
                                    <div className="flex gap-2">
                                        {[25, 50, 100].map(pct => (
                                            <button key={pct} onClick={() => setAmountByPercentage(pct)} className="hover:text-nobel-gold">{pct}%</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <input type="number" placeholder="0.00" value={amountIn} onChange={(e) => setAmountIn(e.target.value)} className="bg-transparent text-3xl font-bold text-stone-900 outline-none w-full placeholder:text-stone-200" />
                                    <div className="flex items-center gap-2 bg-stone-50 px-3 py-1.5 rounded-xl border border-stone-200 shadow-sm shrink-0">
                                        {isBuy ? <img src={inputToken.logoURI} className="w-6 h-6 rounded-full" /> : <img src={token.image} className="w-6 h-6 rounded-full object-cover" />}
                                        <span className="font-bold text-sm text-stone-700">{isBuy ? inputToken.symbol : token.symbol}</span>
                                    </div>
                                </div>
                                <div className="text-[10px] text-stone-400 mt-2 text-right">
                                    Balance: {isConnected ? (isBuy ? balance.toFixed(4) : tokenBalance.toFixed(2)) : '-'}
                                </div>
                            </div>

                            {/* Switcher */}
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                                <button onClick={handleSwapDirection} className="w-10 h-10 bg-stone-50 border-4 border-[#F9F8F4] rounded-xl flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-white transition-all shadow-sm group">
                                    <ArrowUpDown size={18} strokeWidth={2.5} className="group-hover:rotate-180 transition-transform duration-300" />
                                </button>
                            </div>

                            {/* Output */}
                            <div className="bg-white p-4 rounded-2xl border border-stone-200 shadow-sm relative z-0">
                                <div className="flex justify-between text-[10px] font-bold text-stone-400 uppercase mb-2 tracking-wide">
                                    <span>You Receive</span>
                                    {isQuoting && <RefreshCw size={10} className="animate-spin text-stone-400" />}
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                     <div className={`text-3xl font-bold w-full overflow-hidden text-ellipsis transition-colors ${isQuoting ? 'text-stone-300' : 'text-stone-900'}`}>
                                        {estimatedOut}
                                     </div>
                                     <div className="flex items-center gap-2 bg-stone-50 px-3 py-1.5 rounded-xl border border-stone-200 shadow-sm shrink-0">
                                        {!isBuy ? <img src={inputToken.logoURI} className="w-6 h-6 rounded-full" /> : <img src={token.image} className="w-6 h-6 rounded-full object-cover" />}
                                        <span className="font-bold text-sm text-stone-700">{!isBuy ? inputToken.symbol : token.symbol}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Summary Card */}
                        <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3 text-xs font-bold text-stone-400 uppercase tracking-wide pb-2 border-b border-stone-100">
                                <Receipt size={14} /> Transaction Summary
                            </div>
                            <div className="space-y-2 text-xs">
                                <div className="flex justify-between items-center">
                                    <span className="text-stone-500">Rate</span>
                                    <span className="font-mono text-stone-800">1 {isBuy ? inputToken.symbol : token.symbol} ≈ {rate} {isBuy ? token.symbol : inputToken.symbol}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-stone-500">Price Impact</span>
                                    <span className={`font-mono font-bold ${Number(priceImpact) > 5 ? 'text-red-500' : 'text-green-600'}`}>{priceImpact}%</span>
                                </div>
                                {internalQuote ? (
                                    <div className="flex justify-between items-center pt-2 mt-2 border-t border-stone-100">
                                        <span className="text-stone-500 flex items-center gap-1"><Shield size={12} className="text-amber-500"/> Route</span>
                                        <span className="font-mono text-stone-800 font-bold">Bonding Curve</span>
                                    </div>
                                ) : quote?.routePlan && quote.routePlan.length > 0 ? (
                                    <div className="flex justify-between items-center pt-2 mt-2 border-t border-stone-100">
                                        <span className="text-stone-500 flex items-center gap-1"><Zap size={12} /> Route</span>
                                        <span className="font-mono text-stone-800 font-medium">{quote.routePlan[0].protocol}</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 flex items-center gap-2 animate-fade-in font-bold"><Info size={14} className="shrink-0" /> {error}</div>}
                    </div>

                    <div className="p-4 md:p-6 border-t border-stone-200 bg-white sticky bottom-0 z-20">
                        {!isConnected ? (
                            <button onClick={() => connect(baseCurrency as 'SOL'|'SUI', 'Embedded Wallet')} className="w-full py-4 bg-stone-900 text-white rounded-xl font-bold hover:bg-stone-800 transition-colors shadow-lg flex items-center justify-center gap-2"><Wallet size={18} /> Connect Wallet</button>
                        ) : (
                            <button 
                                onClick={() => setShowConfirmation(true)}
                                disabled={!amountIn || isLoading || Number(amountIn) <= 0 || isQuoting}
                                className="w-full py-4 bg-nobel-gold text-stone-900 rounded-xl font-bold hover:bg-[#d4b068] transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group text-lg"
                            >
                                {isLoading ? <><Loader2 className="animate-spin" size={20}/> Processing...</> : <><ArrowRight size={20}/> Review Order</>}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
    </>
  );
};